import { useState, useEffect } from "react";
import { TableBody } from "./Table";
import { TableHeading } from "./Table";
import Table from "Components/Common/Table/Table";
import { usePatientContext } from "Context/Patient";
import { PinnedNotesPanel } from "Components/Patients/EditViewPatient/Forms/DoctorNotes/PinnedNotesPanel";
import {
  VISIT_CHAT_VIEW,
  VISIT_FEEDBACK_VIEW,
  VISIT_NOTES_VIEW,
  VISIT_ORDER_VIEW,
  VISIT_PDFS_VIEW,
  VISIT_TABLE_VIEW,
} from "./ConstantsViews";
import { VisitOrder } from "./VisitOrder";
import { VisitNotes } from "./VisitNotes";
import { VisitFeedback } from "./VisitFeedback";
import { VisitPdfs } from "./VisitPdfs";
import { VisitChat } from "./VisitChat";
import { patientVisitsFormatter } from "Helpers/Patients";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { GetVisitHistory } from "Services/API/Patients.service";
import { PaginationBtns } from "Components/Common/PaginationBtns/PaginationBtns";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import { None } from "Components/Patients/EditViewPatient/Forms/DetailSection/DetailSection";
import "Components/Patients/EditViewPatient/Forms/FormCommonCss/Form.css";
import "Colors.css";
import { ErrorPopUp } from "Components/Common/PopUp/PopUp";

// One page big enough to hold any real patient's history now that the
// Previous/Next controls are gone.
const ALL_ROWS = 200;

export function VisitTable({ setActiveTabIndex }) {
  return (
    <>
      {/* No inline overflow: it would beat the stylesheet and reintroduce the
          inner scrollbar this page just got rid of. */}
      <div className="FormWrapper flex flex-col gap-6">
        <VisitActiveView />
      </div>
    </>
  );
}

const VisitActiveView = () => {
  const { getPatientData } = usePatientContext();
  const [visitsActiveView, setVisitsActiveView] = useState(VISIT_TABLE_VIEW);
  const [patientVisits, setPatientVisits] = useState([]);
  const [patientId, setPatientId] = useState("")
  const [loading, setLoading] = useState(true);
  const [visitId, setVisitId] = useState("");
  const [docName, setDocName] = useState("");
  const [apiError, setApiError] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  useEffect(() => {
    if (getPatientData) {
      const { id } = getPatientData() || {};
      setPatientId(id)
      if (id) {
        setLoading(true);
        GetVisitHistory(id)
          .then((res) => {
            setPatientVisits(patientVisitsFormatter(res?.data));
          })
          .catch((err) => {
            console.error("[Get_Patient_VISITS_History][API_ERROR} -", err);
            setApiError(
              errorFormatter(err?.response?.data?.errors || err?.message)
            );
          })
          .finally(() => {
            setLoading(false);
          });
        return () => {
          setPatientVisits([]);

        }
      }
    }
  }, [getPatientData]);

  useEffect(() => {
    if (getPatientData) {
      const { id } = getPatientData() || {};
      if (id) {
        setLoading(true);
        // No pagination controls on this tab any more, so ask for the whole
        // history in one page rather than stranding older visits on page 2.
        GetVisitHistory(id, 1, ALL_ROWS)
          .then((res) => {
            setLoading(false);
            if (res.data) {
              setPatientVisits(patientVisitsFormatter(res?.data));
              setHasNextPage(res?.pagination?.hasNext);
              setHasPreviousPage(res?.pagination?.hasPrevious);
            }
          })
          .catch((err) => {
            setLoading(false);
            console.error(
              "[PATIENTS_VISITtABLE_ GetVISITSHistory][API_ERROR} -",
              err
            );
            setApiError(err?.response?.data?.errors || err?.message);
          });
      }
    }
  }, [getPatientData, pageNumber]);

  switch (visitsActiveView) {
    case VISIT_ORDER_VIEW:
      return (
        <VisitOrder
          setActiveView={setVisitsActiveView}
          patientId={patientId}
          visitId={visitId}
          docName={docName}
        />
      );
    case VISIT_NOTES_VIEW:
      return (
        <VisitNotes
          setActiveView={setVisitsActiveView}
          patientId={patientId}
          visitId={visitId}
          docName={docName}
        />
      );
    case VISIT_FEEDBACK_VIEW:
      return (
        <VisitFeedback
          setActiveView={setVisitsActiveView}
          patientId={patientId}
          visitId={visitId}
          docName={docName}
        />
      );
    case VISIT_PDFS_VIEW:
      return (
        <VisitPdfs
          setActiveView={setVisitsActiveView}
          patientId={patientId}
          visitId={visitId}
          docName={docName}
        />
      );
    case VISIT_CHAT_VIEW:
      return (
        <VisitChat
          setActiveView={setVisitsActiveView}
          visitId={visitId}
          docName={docName}
        />
      );
    default:
      return (
        <div className="h-full">
          {apiError && <ErrorPopUp error={apiError} setApiError={setApiError} />}

          {/* Provider chart reminders, read-only. Above the visit list so an admin
              opening a patient sees the internal context before the visits. */}
          <PinnedNotesPanel patientId={patientId} />

          <Table
            heading={<TableHeading />}
            body={
              patientVisits.length > 0 ? (
                <TableBody
                  data={patientVisits}
                  onClick={({ id, docName }, nextActiveView) => {
                    setVisitId(id);
                    setDocName(docName);
                    setVisitsActiveView(nextActiveView);
                  }}
                />
              ) : (
                !loading && <None block />
              )
            }
          />
          {loading && <Spinner />}
        </div>
      );
  }
};
